import {Component, OnDestroy, OnInit} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {Subscription} from "rxjs";
import * as fromStore from '../../../store';
import {Store} from '@ngrx/store';
import {filter, first, switchMap} from 'rxjs/operators';
import {Website} from "../website";
import {User} from "../../../user/user";
import {DialogService} from "../../../../ng2-bootstrap-modal/dialog.service";
import {PerformanceService} from "../performance/performance.service";
import {DbResponse} from "../../../shared/interceptors/db-response";
import {ActivityAction, IActivity} from "../../../activities/activity";
import {Observable} from "rxjs";
import {GrantAccessCopyWebsiteDialogComponent} from "./grant-access-website-copy/grant-access-copy-website-dialog.component";
import {TypeUtils} from "../../../shared/utils/type-utils";
import {Location} from "@angular/common";
import {Product} from "../product";
import {WebsiteService} from "../../website.service";
import {HostingDomainData} from "../hosting/domains/hostingDomainData";
import {UserSubscription, UserSubscriptionType} from "../../../user/user-subscription";
import {ManualOptimizationDialogComponent} from "../../manual-optimization-dialog/manual-optimization-dialog.component";
import {HostingService} from "../hosting/hosting.service";
import {OptimizerLoadingComponent} from "../../../optimizer-loading-dialog/optimizer-loading.component";
import {GoogleAnalyticsService} from "../../../shared/services/google-analytics/google-analytics.service";
import {CreateWebsiteService} from "../../../services/local/create-website.service";

@Component({
    selector: 'db-move-to-10web',
    templateUrl: './moveTo10Web.component.html',
    styleUrls: ['./moveTo10Web.component.scss'],
    standalone: false
})
export class MoveTo10WebComponent implements OnInit, OnDestroy {

  private _subscriptions: Subscription[];
  public website: Website;
  public product: Product;
  public user: User;
  public failedResponse: boolean = false;
  public activity: IActivity;
  public _activityProgress: any;
  public loading: boolean;
  public optimizerLoading: boolean;
  public optimizerPlugin: Product;
  public ongoingGoogleScoreActivity$: Observable<IActivity>;
  public userSubscription: UserSubscription;
  public domains: HostingDomainData[];
  public userSubscriptionTypes = UserSubscriptionType;
  public scores = [
    {name: 'Desktop', value: null, loadTime: null},
    {name: 'Mobile', value: null, loadTime: null}
  ];
  public isMobile: boolean;

  public manualOptimize: boolean;
  public manualOptimizationInProgress: boolean;
  public radiusScore: any;

  constructor(
    private _router: Router,
    private _route: ActivatedRoute,
    private _performanceService: PerformanceService,
    private _store: Store<fromStore.DbStoreState>,
    private _location: Location,
    private _websiteService: WebsiteService,
    private _hostingService: HostingService,
    private _dialogService: DialogService,
    private googleAnalyticsService: GoogleAnalyticsService,
    private _createWebsiteService: CreateWebsiteService,
  ) {}

  ngOnInit() {
    this.radiusScore = 4.5 * 3.14 * 25;

    this.isMobile = window.innerWidth <= 991;

    this.user = this._route.snapshot.parent.data['user'];
    this.userSubscription = this._route.snapshot.parent.data['userSubscription'];

    this.website = this._route.snapshot.parent.data['website'];

    this.ongoingGoogleScoreActivity$ = this._store.select(fromStore.getGoogleScoreOngoingActivity(this.website.id));
    this.ongoingGoogleScoreActivity$.subscribe((activity: IActivity) => {
      if (activity && activity.progress >= 0 && !this._activityProgress) {
        this._activityProgress = 0;
        this.simulateLoading();
      }
    });

    this.addEventHandlers();
  }

  ngOnDestroy() {
    this.removeEventHandlers();
  }

  private addEventHandlers(): void {
    this._subscriptions = [];

    if (this.website.domain_hash) {
      this._subscriptions.push(this._store.select(fromStore.getDomains(this.website?.isNetwork && !this.website?.parentId && this.website?.children && this.website?.children.length ? this.website?.children[0] : this.website?.id)).subscribe((domains: HostingDomainData[]) => {
        if (domains.length) {
          this.domains = domains;
        }
      }));
    }

    this._subscriptions.push(
      this._store.select(fromStore.getWebsiteOptimizerPlugin(this.website.id))
        .subscribe((optimizerPlugin): void => {
          if (optimizerPlugin && !TypeUtils.isEmpty(optimizerPlugin)) {
            this.optimizerPlugin = optimizerPlugin;
          }
        })
    );

    this._subscriptions.push(
      this._store
        .select(fromStore.getWebsitesLoaded)
        .pipe(
          filter((loaded: boolean): boolean => !!loaded),
          switchMap(() => this._store.select(fromStore.getWebsite(this.website.id)))
        )
        .subscribe((website: Website) => {
          if (website) {
            this.website = website;
            this.calculateScore();
          }
        })
    );

  }

  private removeEventHandlers(): void {
    this._subscriptions.forEach((subscription: Subscription): void => {
      subscription.unsubscribe();
    });
    this._subscriptions = null;
  }

  public migrate(): void {
    this._createWebsiteService.openMigrateWebsiteModal(this.website.id, this.website.site_url);
  }

  public manualOptimization(): void {
    this._hostingService.manualOptimize(this.website.id)
      .pipe(filter((response: DbResponse) => !!response),
      first())
      .subscribe((response: DbResponse) => {
        if (response.success) {
          this._store.dispatch( new fromStore.LoadWebsites() );
          this._dialogService.addDialog(ManualOptimizationDialogComponent);
        }
      });
  }

  public installOptimizerPlugin(): void {
    this.optimizerLoading = true;
    const network = 1;
    this._store.dispatch(new fromStore.InitWebsiteProductAction({
      website: this.website,
      products: [this.optimizerPlugin],
      action: ActivityAction.INSTALL_ACTIVATE,
      network: network
    }));
    this._dialogService.addDialog(OptimizerLoadingComponent, {websiteId: this.website.id})
      .subscribe((isConfirmed: boolean) => {
        this._performanceService.runWarmUpPerformance(this.website.id)
        .pipe(filter((response: DbResponse): boolean => !!response),
          first())
          .subscribe((response: DbResponse): void => {
            this.optimizerLoading = false;
            this.retry();
          });
      });
  }



  public retry(): void {
    this.failedResponse = false;
    this.loading = true;
    this._activityProgress = 0;
    this.simulateLoading();
    this._performanceService.getGooglePageSpeedScore(this.website.id)
      .pipe(filter((response: DbResponse): boolean => !!response),
      first())
      .subscribe((response: DbResponse): void => {
        if (!response.success) {
          this.failedResponse = true;
        }
        this._activityProgress = 100;
        this.loading = false;
      });
  }
  public grantAccess(): void {
    this._dialogService.addDialog(GrantAccessCopyWebsiteDialogComponent, {
      websiteId: this.website.id,
      websiteUrl: this.website.site_url
    });
  }

  private simulateLoading() {
    const interval = setInterval((): void => {
      const previousActivity = this._activityProgress || 0;
      if (previousActivity === 60 || previousActivity >= 98) {
        clearInterval(interval);
        const _interval = setInterval((): void => {
          const _previousActivity = this._activityProgress || 0;
          if (_previousActivity >= 98) {
            clearInterval(_interval);
            return;
          }
          let _nextProgress: number;
          _nextProgress = _previousActivity + 1;

          if (_nextProgress >= 100) {
            this._activityProgress = 100;
          }
          else {
            this._activityProgress = _nextProgress;
          }
        }, 700);
      }
      let nextProgress: number;
      nextProgress = previousActivity + 1;


      if (nextProgress >= 100) {
        this._activityProgress = 100;
      }
      else {
        this._activityProgress = nextProgress;
      }
    }, 50);
  }


  public sendEvent(from, action = null) {
    const analyticsData = {
      eventCategory: 'Dashboard Action',
      eventAction: action || 'Website Admin',
      eventLabel: from,
      websiteId: this.website && this.website.id
    };
    this.googleAnalyticsService.sendAnalyticsEventWithCore(analyticsData);
  }

  private calculateScore(): void {
    this.scores[0].value = this.website.score && this.website.score.currentScore && !TypeUtils.isNull(this.website.score.currentScore.desktopScore) ? this.website.score.currentScore.desktopScore : (this.website.score && this.website.score.previousScore && !TypeUtils.isNull(this.website.score.previousScore.desktopScore)) ? this.website.score.previousScore.desktopScore : null;
    this.scores[1].value = this.website.score && this.website.score.currentScore && !TypeUtils.isNull(this.website.score.currentScore.mobileScore) ? this.website.score.currentScore.mobileScore : (this.website.score && this.website.score.previousScore && !TypeUtils.isNull(this.website.score.previousScore.mobileScore)) ? this.website.score.previousScore.mobileScore : null;
    this.scores[0].loadTime = this.website.score && this.website.score.currentScore && !TypeUtils.isNull(this.website.score.currentScore.desktopTTI) ? this.website.score.currentScore.desktopTTI : (this.website.score && this.website.score.previousScore && !TypeUtils.isNull(this.website.score.previousScore.desktopTTI)) ? this.website.score.previousScore.desktopTTI : null;
    this.scores[1].loadTime = this.website.score && this.website.score.currentScore && !TypeUtils.isNull(this.website.score.currentScore.mobileTTI) ? this.website.score.currentScore.mobileTTI : (this.website.score && this.website.score.previousScore && !TypeUtils.isNull(this.website.score.previousScore.mobileTTI)) ? this.website.score.previousScore.mobileTTI : null;
    this.manualOptimize = !!(this.scores[0].value < 90 && this.website && this.website.migrated_from && this.website.domain_hash);
    this.manualOptimizationInProgress = this.website && this.website.migrated_from && this.website.domain_hash && (this.website.manualOptimizationStatus === "accepted" || this.website.manualOptimizationStatus === "in_progress");
  }

}
